import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { Plus, Trash2, Copy, Search, MessageSquare, Send, Banknote, RefreshCw, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Knowledge() {
  const { user } = useAuth();
  const canEdit = ["admin", "supervisor"].includes(user?.role);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formats, setFormats] = useState(null);
  const [copied, setCopied] = useState("");

  const load = async () => {
    const { data } = await api.get("/templates", { params: lang ? { language: lang } : {} });
    setList(data);
  };

  useEffect(() => { load(); }, [lang]);
  useEffect(() => {
    api.get("/telegram/complaint-formats").then((r) => setFormats(r.data));
  }, []);

  const filtered = list.filter((t) =>
    !q || t.label.toLowerCase().includes(q.toLowerCase()) || t.code.toLowerCase().includes(q.toLowerCase()) || t.body.toLowerCase().includes(q.toLowerCase())
  );

  const copyFormat = async (lng) => {
    try {
      await navigator.clipboard.writeText(formats[lng]);
      setCopied(lng);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      // Fallback if clipboard API unavailable
      const ta = document.createElement("textarea");
      ta.value = formats[lng];
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(lng); setTimeout(() => setCopied(""), 1500); } catch (_e) { /* execCommand unsupported */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="knowledge-page">
      <Panel
        title="Customer Complaint Format · Share to Telegram/WhatsApp Group"
        testid="complaint-format-panel"
      >
        <p className="text-xs text-slate-400 mb-3 max-w-3xl">
          Bagikan template ini ke customer (atau ketik <span className="font-mono text-cyan">/format</span> di group Telegram — bot akan auto-reply). Pesan yang mengikuti format ini akan otomatis ter-parse jadi tiket dengan field order/RRN/amount/service/status terisi.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="complaint-format-grid">
          {formats && ["ID", "ZH", "EN"].map((lng) => (
            <div key={lng} className="bg-bg border border-line rounded-sm p-3" data-testid={`complaint-format-${lng}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-cyan" />
                  <span className="text-xs font-semibold">
                    {lng === "ID" ? "Indonesia" : lng === "ZH" ? "中文" : "English"}
                  </span>
                  <Badge color={lng === "ID" ? "info" : lng === "ZH" ? "awaiting" : "success"}>{lng}</Badge>
                </div>
                <button
                  data-testid={`copy-format-${lng}`}
                  onClick={() => copyFormat(lng)}
                  className={`text-[10px] uppercase tracking-wider border px-2 py-1 rounded-sm flex items-center gap-1 transition-colors ${
                    copied === lng ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-line hover:bg-surface2 text-slate-400"
                  }`}
                >
                  <Copy className="w-3 h-3" /> {copied === lng ? "Copied!" : "Copy"}
                </button>
              </div>
              <pre className="text-[11px] text-slate-300 whitespace-pre-wrap font-sans max-h-72 overflow-y-auto leading-relaxed">{formats[lng]}</pre>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px] text-slate-500 border-t border-line pt-2 flex items-center gap-2">
          <Send className="w-3 h-3 text-cyan" />
          <span>Tip: kirim <span className="font-mono text-cyan">/format en</span> atau <span className="font-mono text-cyan">/format zh</span> di group untuk minta versi bahasa lain.</span>
        </div>
      </Panel>

      <TarapayBankSupport />

      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Knowledge Base</div>
          <h1 className="text-2xl font-bold tracking-tight">Response Templates</h1>
          <p className="text-xs text-slate-400 mt-1">Template balasan resmi untuk transaksi QRIS, VA, dan Gaming. Pakai placeholder <span className="font-mono text-cyan">{"{ORDER}"}</span>, <span className="font-mono text-cyan">{"{RRN}"}</span>, <span className="font-mono text-cyan">{"{VA}"}</span>.</p>
        </div>
        {canEdit && (
          <button data-testid="new-template-btn" onClick={() => { setCreating(true); setEditing({ code: "", label: "", language: "ID", body: "", category: "general" }); }} className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark">
            <Plus className="w-4 h-4" /> New Template
          </button>
        )}
      </div>

      <div className="bg-surface border border-line rounded-sm p-3 flex flex-wrap gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} className={`${ipt} pl-9`} placeholder="Search templates..." />
        </div>
        <select data-testid="kb-lang-filter" value={lang} onChange={(e) => setLang(e.target.value)} className={ipt + " max-w-[160px]"}>
          <option value="">All Languages</option>
          <option value="ID">Indonesia</option>
          <option value="ZH">中文</option>
          <option value="EN">English</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((t) => (
          <div key={t.id} className="bg-surface border border-line rounded-sm p-3 hover:border-cyan/40 transition-colors" data-testid={`kb-card-${t.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{t.code} · {t.language}</div>
              </div>
              <Badge color={t.language === "ID" ? "info" : t.language === "ZH" ? "awaiting" : "success"}>{t.language}</Badge>
            </div>
            <pre className="text-xs text-slate-200 whitespace-pre-wrap font-sans bg-bg border border-line/50 rounded-sm p-2.5 max-h-40 overflow-y-auto">{t.body}</pre>
            <div className="flex justify-end gap-2 mt-2">
              <button data-testid={`kb-copy-${t.id}`} onClick={() => navigator.clipboard.writeText(t.body)} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
              {canEdit && (
                <>
                  <button data-testid={`kb-edit-${t.id}`} onClick={() => { setEditing(t); setCreating(false); }} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 text-cyan">Edit</button>
                  <button data-testid={`kb-delete-${t.id}`} onClick={async () => { if (confirm("Delete template?")) { await api.delete(`/templates/${t.id}`); load(); } }} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-rose-500/20 text-rose-400 flex items-center gap-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-8 text-slate-500 text-sm">No templates match.</div>}
      </div>

      {editing && (
        <TemplateModal tpl={editing} creating={creating} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function TemplateModal({ tpl, creating, onClose, onSaved }) {
  const [f, setF] = useState(tpl);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (creating) await api.post("/templates", f);
      else await api.patch(`/templates/${f.id}`, f);
      onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="template-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-2xl space-y-3">
        <h3 className="text-lg font-bold">{creating ? "New Template" : "Edit Template"}</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Code</label>
            <input data-testid="tpl-modal-code" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className={`${ipt} font-mono uppercase`} placeholder="QRIS_SUCCESS" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Language</label>
            <select data-testid="tpl-modal-lang" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} className={ipt}>
              <option value="ID">Indonesia</option>
              <option value="ZH">中文</option>
              <option value="EN">English</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Category</label>
            <select data-testid="tpl-modal-cat" value={f.category || "general"} onChange={(e) => setF({ ...f, category: e.target.value })} className={ipt}>
              <option value="general">General</option>
              <option value="qris">QRIS</option>
              <option value="va">VA</option>
              <option value="gaming">Gaming</option>
              <option value="payout">Payout</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Label</label>
          <input data-testid="tpl-modal-label" required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className={ipt} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Body</label>
          <textarea data-testid="tpl-modal-body" required rows={9} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${ipt} font-mono`} />
        </div>
        {err && <div className="text-xs text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Cancel</button>
          <button data-testid="tpl-modal-save" disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}

function TarapayBankSupport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchList = async (force = false) => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/tarapay/bank-list", { params: force ? { force_refresh: true } : {} });
      setData(r.data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(false); }, []);

  const banks = (data?.banks || []).filter((b) => !q || (b.bank_name || "").toLowerCase().includes(q.toLowerCase()));
  const ewallets = (data?.ewallets || []).filter((b) => !q || (b.bank_name || "").toLowerCase().includes(q.toLowerCase()));
  const total = (data?.banks?.length || 0) + (data?.ewallets?.length || 0);
  const errors = data?.errors || [];

  const copyAll = async () => {
    if (!data) return;
    const lines = ["Daftar Bank/E-wallet yang DIDUKUNG Tarapay:", ""];
    if (data.banks?.length) {
      lines.push("=== BANK / VA ===");
      data.banks.forEach((b) => lines.push(`- ${b.bank_name} (ID: ${b.bank_id})`));
      lines.push("");
    }
    if (data.ewallets?.length) {
      lines.push("=== E-WALLET ===");
      data.ewallets.forEach((b) => lines.push(`- ${b.bank_name} (ID: ${b.bank_id})`));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* clipboard unavailable */ }
  };

  return (
    <Panel
      title="Tarapay — Supported Banks & E-Wallets (Live)"
      testid="tarapay-banks-panel"
      action={
        <div className="flex items-center gap-2">
          {data?.fetched_at && (
            <span className="text-[10px] text-slate-500" data-testid="tarapay-banks-fetched-at">
              Updated: {new Date(data.fetched_at).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" })}
            </span>
          )}
          <button
            data-testid="tarapay-banks-copy"
            onClick={copyAll}
            disabled={!total}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm flex items-center gap-1 transition-colors ${
              copied ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-line hover:bg-surface2 text-slate-400 disabled:opacity-40"
            }`}
          >
            <Copy className="w-3 h-3" /> {copied ? "Copied!" : "Copy All"}
          </button>
          <button
            data-testid="tarapay-banks-refresh"
            onClick={() => fetchList(true)}
            disabled={loading}
            className="text-[10px] uppercase tracking-wider px-2 py-1 border border-line hover:bg-surface2 rounded-sm flex items-center gap-1 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </button>
        </div>
      }
    >
      <p className="text-xs text-slate-400 mb-3 max-w-3xl flex items-start gap-2">
        <Banknote className="w-3.5 h-3.5 text-cyan flex-shrink-0 mt-0.5" />
        Daftar real-time bank & e-wallet yang didukung Tarapay. Cek nama bank yang customer tanyakan — kalau ada di list → sampaikan &quot;didukung&quot;, kalau tidak ada → &quot;saat ini belum didukung&quot;. Data di-cache 12 jam, klik Refresh untuk paksa fetch ulang.
      </p>

      <div className="mb-3 relative max-w-sm">
        <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="tarapay-banks-search"
          placeholder="Cari nama bank / e-wallet..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-bg border border-line rounded-sm pl-7 pr-3 py-1.5 text-xs focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40"
        />
      </div>

      {err && (
        <div className="px-3 py-2 mb-3 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm flex items-start gap-2" data-testid="tarapay-banks-error">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {err}
        </div>
      )}

      {errors.length > 0 && (
        <div className="px-3 py-2 mb-3 border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs rounded-sm flex items-start gap-2" data-testid="tarapay-banks-api-error">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Tarapay API mengembalikan error</div>
            {errors.map((e, i) => <div key={i} className="text-[11px] mt-0.5">type {e.type}: {e.message}</div>)}
            <div className="text-[10px] text-amber-300/70 mt-1">Tips: kalau &quot;IP not in whitelist&quot; → ini bisa dari env yang IP-nya belum di-whitelist Tarapay (mis. dev/preview). Production seharusnya OK.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="tarapay-banks-bank-list">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan mb-2 flex items-center gap-1.5">
            <Banknote className="w-3 h-3" /> Bank / Virtual Account ({banks.length})
          </div>
          <div className="border border-line rounded-sm divide-y divide-line/40 max-h-[420px] overflow-y-auto">
            {banks.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "—"}
              </div>
            ) : banks.map((b) => (
              <div key={b.bank_id} className="px-3 py-1.5 flex items-center justify-between hover:bg-surface2 text-xs" data-testid={`bank-${b.bank_id}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <span className="font-semibold text-slate-200">{b.bank_name}</span>
                </div>
                <span className="font-mono text-[10px] text-slate-500">#{b.bank_id}</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="tarapay-banks-ewallet-list">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan mb-2 flex items-center gap-1.5">
            <Banknote className="w-3 h-3" /> E-Wallet ({ewallets.length})
          </div>
          <div className="border border-line rounded-sm divide-y divide-line/40 max-h-[420px] overflow-y-auto">
            {ewallets.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "—"}
              </div>
            ) : ewallets.map((b) => (
              <div key={b.bank_id} className="px-3 py-1.5 flex items-center justify-between hover:bg-surface2 text-xs" data-testid={`ewallet-${b.bank_id}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <span className="font-semibold text-slate-200">{b.bank_name}</span>
                </div>
                <span className="font-mono text-[10px] text-slate-500">#{b.bank_id}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
}

